import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle, Edit3, Trash2, Save, X, Database, Search, Clock, Check, XCircle, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DatasetManagement = () => {
  const [corrections, setCorrections] = useState([]);
  const [filteredCorrections, setFilteredCorrections] = useState([]);
  const [pendingCorrections, setPendingCorrections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingPendingId, setEditingPendingId] = useState(null);
  const [editForm, setEditForm] = useState({ user_query: '', original_response: '', corrected_response: '', timestamp: '' });
  const [pendingEditForm, setPendingEditForm] = useState({ user_query: '', original_response: '', corrected_response: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('pending');

  const loadDataset = async () => {
    setIsLoading(true);
    try {
      const [datasetRes, pendingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dataset`),
        axios.get(`${BACKEND_URL}/api/pending-corrections`)
      ]);
      const data = datasetRes.data.corrections || [];
      setCorrections(data);
      setFilteredCorrections(data);
      setPendingCorrections(pendingRes.data.pending || []);
      setStatusMessage({ type: 'success', message: `${data.length} corrections, ${pendingRes.data.total || 0} en attente` });
    } catch (error) {
      console.error('Erreur chargement:', error);
      setStatusMessage({ type: 'error', message: 'Erreur lors du chargement' });
    } finally { setIsLoading(false); }
  };

  useEffect(() => { loadDataset(); }, []);

  useEffect(() => {
    if (!searchQuery.trim()) { setFilteredCorrections(corrections); return; }
    const q = searchQuery.toLowerCase();
    setFilteredCorrections(corrections.filter(c =>
      c.user_query?.toLowerCase().includes(q) || c.original_response?.toLowerCase().includes(q) || c.corrected_response?.toLowerCase().includes(q)
    ));
  }, [searchQuery, corrections]);

  // === Dataset validé ===
  const handleEdit = (correction) => {
    setEditingId(correction._id || correction.id);
    setEditForm({ user_query: correction.user_query || '', original_response: correction.original_response || '', corrected_response: correction.corrected_response || '', timestamp: correction.timestamp || new Date().toISOString() });
  };
  const handleSave = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/dataset/${editingId}`, editForm, { headers: { 'Content-Type': 'application/json' } });
      setStatusMessage({ type: 'success', message: 'Correction mise à jour' });
      setEditingId(null);
      loadDataset();
    } catch (error) {
      setStatusMessage({ type: 'error', message: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };
  const handleDelete = async (correctionId) => {
    if (!window.confirm('Supprimer cette correction du dataset ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/dataset/${correctionId}`);
      setStatusMessage({ type: 'success', message: 'Correction supprimée' });
      loadDataset();
    } catch (error) {
      setStatusMessage({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  };

  // === Corrections en attente ===
  const handleEditPending = (correction) => {
    setEditingPendingId(correction.id);
    setPendingEditForm({ user_query: correction.user_query || '', original_response: correction.original_response || '', corrected_response: correction.corrected_response || '' });
  };
  const handleSavePending = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/pending-corrections/${editingPendingId}`, pendingEditForm);
      setStatusMessage({ type: 'success', message: 'Correction modifiée' });
      setEditingPendingId(null);
      loadDataset();
    } catch (error) {
      setStatusMessage({ type: 'error', message: 'Erreur lors de la modification' });
    }
  };
  const handleValidate = async (correctionId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/pending-corrections/${correctionId}/validate`);
      setStatusMessage({ type: 'success', message: 'Correction validée et ajoutée au dataset' });
      loadDataset();
    } catch (error) {
      setStatusMessage({ type: 'error', message: 'Erreur lors de la validation' });
    }
  };
  const handleReject = async (correctionId) => {
    if (!window.confirm('Rejeter cette correction ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/pending-corrections/${correctionId}`);
      setStatusMessage({ type: 'success', message: 'Correction rejetée' });
      loadDataset();
    } catch (error) {
      setStatusMessage({ type: 'error', message: 'Erreur lors du rejet' });
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {statusMessage && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
          statusMessage.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {statusMessage.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          <span>{statusMessage.message}</span>
        </div>
      )}

      {/* Toggle sections */}
      <div className="flex gap-2">
        <Button
          variant={activeSection === 'pending' ? 'default' : 'outline'}
          onClick={() => setActiveSection('pending')}
          className={activeSection === 'pending' ? 'bg-amber-500 hover:bg-amber-600' : ''}
          data-testid="pending-tab"
        >
          <Clock className="w-4 h-4 mr-2" />
          En attente
          {pendingCorrections.length > 0 && (
            <Badge className="ml-2 bg-red-500 text-white border-0 text-xs">{pendingCorrections.length}</Badge>
          )}
        </Button>
        <Button
          variant={activeSection === 'validated' ? 'default' : 'outline'}
          onClick={() => setActiveSection('validated')}
          className={activeSection === 'validated' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}
          data-testid="validated-tab"
        >
          <Database className="w-4 h-4 mr-2" />
          Dataset validé
          <Badge variant="secondary" className="ml-2">{corrections.length}</Badge>
        </Button>
        <Button variant="outline" size="sm" onClick={loadDataset} disabled={isLoading} className="ml-auto">
          {isLoading ? 'Chargement...' : 'Actualiser'}
        </Button>
      </div>

      {/* === Section EN ATTENTE === */}
      {activeSection === 'pending' && (
        <div className="space-y-4">
          {pendingCorrections.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="py-8 text-center text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Aucune correction en attente de validation
              </CardContent>
            </Card>
          ) : (
            pendingCorrections.map(correction => {
              const isEditing = editingPendingId === correction.id;
              return (
                <Card key={correction.id} className="border-l-4 border-l-amber-400 border border-amber-200 bg-amber-50/30" data-testid={`pending-${correction.id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300">En attente</Badge>
                      {correction.submitted_by && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <User className="w-3 h-3" />
                          <span>{correction.submitted_by}</span>
                        </div>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">
                        {new Date(correction.created_at).toLocaleString('fr-FR')}
                      </span>
                    </div>

                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Question utilisateur</Label>
                          <Textarea value={pendingEditForm.user_query} onChange={(e) => setPendingEditForm({...pendingEditForm, user_query: e.target.value})} className="mt-1 min-h-[60px]" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Réponse originale</Label>
                          <Textarea value={pendingEditForm.original_response} onChange={(e) => setPendingEditForm({...pendingEditForm, original_response: e.target.value})} className="mt-1 min-h-[100px]" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Réponse corrigée</Label>
                          <Textarea value={pendingEditForm.corrected_response} onChange={(e) => setPendingEditForm({...pendingEditForm, corrected_response: e.target.value})} className="mt-1 min-h-[100px]" />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingPendingId(null)}><X className="w-4 h-4 mr-1" />Annuler</Button>
                          <Button size="sm" onClick={handleSavePending} className="bg-cyan-500 hover:bg-cyan-600"><Save className="w-4 h-4 mr-1" />Enregistrer</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700 mb-1">Question</Badge>
                          <p className="text-sm text-slate-700 bg-white p-2.5 rounded-lg border border-slate-200">{correction.user_query}</p>
                        </div>
                        <div>
                          <Badge variant="outline" className="text-xs bg-orange-50 border-orange-200 text-orange-700 mb-1">Réponse originale</Badge>
                          <p className="text-sm text-slate-700 bg-white p-2.5 rounded-lg border border-slate-200 whitespace-pre-wrap max-h-40 overflow-y-auto">{correction.original_response}</p>
                        </div>
                        <div>
                          <Badge variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700 mb-1">Correction proposée</Badge>
                          <p className="text-sm text-slate-700 bg-white p-2.5 rounded-lg border border-slate-200 whitespace-pre-wrap max-h-40 overflow-y-auto">{correction.corrected_response}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-amber-200">
                          <Button size="sm" onClick={() => handleValidate(correction.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`validate-${correction.id}`}>
                            <Check className="w-4 h-4 mr-1" />Valider
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEditPending(correction)} className="hover:bg-cyan-50" data-testid={`edit-pending-${correction.id}`}>
                            <Edit3 className="w-4 h-4 mr-1" />Modifier
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReject(correction.id)} className="hover:bg-red-50 hover:text-red-600 hover:border-red-300" data-testid={`reject-${correction.id}`}>
                            <XCircle className="w-4 h-4 mr-1" />Rejeter
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* === Section DATASET VALIDÉ === */}
      {activeSection === 'validated' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Rechercher dans le dataset..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
            </CardContent>
          </Card>

          {isLoading ? (
            <div className="text-center py-8 text-slate-500">Chargement...</div>
          ) : filteredCorrections.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              {searchQuery ? 'Aucune correction trouvée' : 'Aucune correction dans le dataset'}
            </div>
          ) : (
            filteredCorrections.map(correction => {
              const isEditing = editingId === (correction._id || correction.id);
              return (
                <Card key={correction._id || correction.id} className="border border-slate-200">
                  <CardContent className="p-4 sm:p-6">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Question</Label>
                          <Textarea value={editForm.user_query} onChange={(e) => setEditForm({...editForm, user_query: e.target.value})} className="mt-1 min-h-[60px]" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Réponse originale</Label>
                          <Textarea value={editForm.original_response} onChange={(e) => setEditForm({...editForm, original_response: e.target.value})} className="mt-1 min-h-[100px]" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Réponse corrigée</Label>
                          <Textarea value={editForm.corrected_response} onChange={(e) => setEditForm({...editForm, corrected_response: e.target.value})} className="mt-1 min-h-[100px]" />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingId(null)}><X className="w-4 h-4 mr-1" />Annuler</Button>
                          <Button size="sm" onClick={handleSave} className="bg-cyan-500 hover:bg-cyan-600"><Save className="w-4 h-4 mr-1" />Sauvegarder</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 space-y-3 min-w-0">
                            {correction.submitted_by && (
                              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                                <User className="w-3 h-3" />
                                <span>Par <span className="font-medium text-slate-700">{correction.submitted_by}</span></span>
                              </div>
                            )}
                            <div>
                              <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700 mb-1">Question</Badge>
                              <p className="text-sm text-slate-700 bg-blue-50/50 p-2.5 rounded-lg">{correction.user_query}</p>
                            </div>
                            <div>
                              <Badge variant="outline" className="text-xs bg-orange-50 border-orange-200 text-orange-700 mb-1">Réponse originale</Badge>
                              <p className="text-sm text-slate-700 bg-orange-50/50 p-2.5 rounded-lg whitespace-pre-wrap max-h-40 overflow-y-auto">{correction.original_response}</p>
                            </div>
                            <div>
                              <Badge variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700 mb-1">Réponse corrigée</Badge>
                              <p className="text-sm text-slate-700 bg-green-50/50 p-2.5 rounded-lg whitespace-pre-wrap max-h-40 overflow-y-auto">{correction.corrected_response}</p>
                            </div>
                            <div className="text-xs text-slate-400">
                              {new Date(correction.timestamp || correction.created_at).toLocaleString('fr-FR')}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1.5 flex-shrink-0">
                            <Button variant="outline" size="sm" onClick={() => handleEdit(correction)} className="hover:bg-cyan-50 h-8 w-8 p-0"><Edit3 className="w-4 h-4" /></Button>
                            <Button variant="outline" size="sm" onClick={() => handleDelete(correction._id || correction.id)} className="hover:bg-red-50 hover:text-red-600 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default DatasetManagement;
